// client/src/components/MerchantSettings.js

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { firestore, storage } from '../../firebase';
import {
  doc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
  getDocFromCache, // [COST-OPT] lire le cache Firestore si dispo (0 read réseau)
  limit as fsLimit, // [COST-OPT] limiter les requêtes
} from 'firebase/firestore';
import {
  getAuth,
  onAuthStateChanged,
  updateEmail as firebaseUpdateEmail,
  reauthenticateWithCredential,
  EmailAuthProvider
} from 'firebase/auth';
import { ref, getDownloadURL, listAll, uploadBytes, getMetadata } from 'firebase/storage';
import '../CSS/Dashboard.css';
import { useNavigate, Link } from 'react-router-dom';
import MerchantNavbar from './MerchantNavbar';
import { LanguageSelector } from '../../i18n';
import { useTranslation } from 'react-i18next';
import { FaUser } from 'react-icons/fa';
import { FiEdit2, FiChevronRight } from 'react-icons/fi';
import AccountSwitcher from '../DIVERS/AccountSwitcher';
import { logoutSafely } from '../../utils/logoutSafely';

// === caches & helpers =======================================================
const initialSettings = JSON.parse(
  localStorage.getItem('Settings') || '{"email":"","pseudo":""}'
);

// [COST-OPT] Cache 24h pour l'URL photo de profil
const logoKey = (uid) => `profilePhotoURL_${uid}`;
const LOGO_TTL = 24 * 3600 * 1000;

// [COST-OPT] Cache 1h pour invitations
const invitesKey = (email) => `invites_${email}`;
const INVITES_TTL = 3600 * 1000;

// [COST-OPT] Cache 24h pour base d'abonnement
const subKey = (uid) => `sub_base_${uid}`;
const SUB_TTL = 24 * 3600 * 1000;

// Égalité superficielle pour éviter des setState inutiles
const shallowEqual = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  for (const k of ka) if (a[k] !== b[k]) return false;
  return true;
};

// Debounce petit
function debounce(fn, delay = 800) {
  let t;
  const w = (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), delay);
  };
  w.cancel = () => clearTimeout(t);
  return w;
}

// ===========================================================================

const MerchantSettings = () => {
  const [user, setUser] = useState(initialSettings);
  const [newEmail, setNewEmail] = useState('');
  const [isEditingEmail, setIsEditingEmail] = useState(false);

  const [newPseudo, setNewPseudo] = useState('');
  const [isEditingPseudo, setIsEditingPseudo] = useState(false);

  // eslint-disable-next-line
  const [invitations, setInvitations] = useState([]);
  const [showModal, setShowModal] = useState(false);

  // [COST-OPT] on charge d'abord un cache générique (sans uid) si présent
  const [profilePhotoURL, setProfilePhotoURL] = useState(
    localStorage.getItem('profilePhotoURL') || null
  );
  // eslint-disable-next-line
  const [subscriptionBase, setSubscriptionBase] = useState(null);

  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  // eslint-disable-next-line
  const { t } = useTranslation();

  const auth = useMemo(() => getAuth(), []); // [COST-OPT] évite re-créer l'instance

  // --- Fonction de sanitisation ---
  const sanitizeInput = input => String(input).replace(/(['";]|--)/g, '');

  // Charge les Settings depuis localStorage (déjà effectué via initialSettings)
  useEffect(() => {
    const stored = localStorage.getItem('Settings');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const next = { email: parsed.email, pseudo: parsed.pseudo };
        setUser(prev => (shallowEqual(prev, next) ? prev : next)); // [COST-OPT]
      } catch (err) {
        console.error('Erreur parsing Settings:', err);
      }
    }
  }, []);

  // Récupère la photo de profil la plus récente (avec cache TTL)
  const fetchProfilePhoto = useCallback(async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    // 1) Essayer cache frais (par uid)
    try {
      const raw = localStorage.getItem(logoKey(uid));
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.url && parsed?.ts && Date.now() - parsed.ts < LOGO_TTL) {
          setProfilePhotoURL(parsed.url);
          return; // [COST-OPT] pas d'appel Storage
        } else if (parsed?.url) {
          // affiche l'ancien le temps du refresh
          setProfilePhotoURL(parsed.url);
        }
      }
    } catch {}

    const folderRef = ref(storage, `${uid}/photo-profil/`);
    try {
      const list = await listAll(folderRef);
      if (list.items.length) {
        // [COST-OPT] si fichiers préfixés par timestamp, on évite getMetadata
        let latest = null;
        const byTs = list.items
          .map(it => ({ it, ts: Number((it.name || '').split('_')[0]) }))
          .filter(x => Number.isFinite(x.ts))
          .sort((a, b) => b.ts - a.ts);

        if (byTs.length) {
          latest = byTs[0].it;
        } else {
          // fallback: lire métadonnées (coûteux, mais rare)
          const items = await Promise.all(
            list.items.map(async item => {
              try {
                const meta = await getMetadata(item);
                return { item, time: new Date(meta.timeCreated) };
              } catch {
                return { item, time: new Date(0) };
              }
            })
          );
          items.sort((a, b) => b.time - a.time);
          latest = items[0].item;
        }

        const url = await getDownloadURL(latest);
        setProfilePhotoURL(url);
        // Persist caches (global & par uid)
        try {
          localStorage.setItem(logoKey(uid), JSON.stringify({ url, ts: Date.now() }));
          localStorage.setItem('profilePhotoURL', url);
        } catch {}
      } else {
        setProfilePhotoURL(null);
        try {
          localStorage.removeItem(logoKey(uid));
          localStorage.removeItem('profilePhotoURL');
        } catch {}
      }
    } catch (err) {
      console.error('Erreur fetchProfilePhoto:', err);
      setProfilePhotoURL(null);
    }
  }, [auth]);

  // Upload nouvelle photo
  const handleProfilePhotoChange = async e => {
    const file = e.target.files?.[0];
    const uid = auth.currentUser?.uid;
    if (!file || !uid) return;
    const safeName = sanitizeInput(file.name);
    const path = `${uid}/photo-profil/${Date.now()}_${safeName}`;
    const fileRef = ref(storage, path);
    try {
      await uploadBytes(fileRef, file);
      // invalider cache pour forcer vrai refresh
      try { localStorage.removeItem(logoKey(uid)); } catch {}
      fetchProfilePhoto();
    } catch (err) {
      console.error('Erreur upload photo:', err);
    }
  };

  // Observe Auth + Firestore user doc (avec cache + limit pour stripe)
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, async (userAuth) => {
      if (!userAuth) {
        navigate('/');
        return;
      }
      const uid = userAuth.uid;
      const userRef = doc(firestore, 'users', uid);

      // [COST-OPT] hydrate depuis cache Firestore si dispo (0 read réseau)
      try {
        const cached = await getDocFromCache(userRef);
        if (cached?.exists()) {
          const data = cached.data() || {};
          const next = { email: data.email || '', pseudo: data.pseudo || '' };
          setUser(prev => (shallowEqual(prev, next) ? prev : next));
          if (!data.pseudo) setShowModal(true);
        }
      } catch {}

      const unsubSnap = onSnapshot(userRef, snap => {
        const data = snap.data();
        if (!data) return;
        const next = { email: data.email || '', pseudo: data.pseudo || '' };
        setUser(prev => (shallowEqual(prev, next) ? prev : next)); // [COST-OPT]
        localStorage.setItem('Settings', JSON.stringify(next));
        if (!data.pseudo) setShowModal(true);
        // Invitations basées sur email — passé au debounced fetch (avec cache 1h)
        if (data.email) debouncedFetchInvitations(data.email);
      });

      // Récupère le statut d'abonnement (limit + cache 24h)
      const subCacheRaw = localStorage.getItem(subKey(uid));
      let useCache = false;
      if (subCacheRaw) {
        try {
          const parsed = JSON.parse(subCacheRaw);
          if (parsed?.base && parsed?.ts && Date.now() - parsed.ts < SUB_TTL) {
            setSubscriptionBase(parsed.base);
            useCache = true;
          }
        } catch {}
      }
      if (!useCache) {
        (async () => {
          try {
            const qSub = query(
              collection(firestore, 'stripecustomers'),
              where('uid', '==', uid),
              fsLimit(1) // [COST-OPT] on ne lit que le premier
            );
            const snap = await getDocs(qSub);
            if (!snap.empty) {
              const base = snap.docs[0].data().base;
              setSubscriptionBase(base);
              try {
                localStorage.setItem(subKey(uid), JSON.stringify({ base, ts: Date.now() }));
              } catch {}
            }
          } catch (e) {
            console.error('Erreur lecture abonnement:', e);
          }
        })();
      }

      fetchProfilePhoto();

      return () => unsubSnap();
    });

    return () => unsubAuth();
    // eslint-disable-next-line
  }, [navigate, fetchProfilePhoto, auth]);

  // Ré-authentifie l'utilisateur (prompt de mot de passe)
  const reauthenticate = async currentUser => {
    const password = window.prompt('Veuillez entrer votre mot de passe actuel :');
    if (!password) throw new Error('Mot de passe requis pour ré-authentification.');
    const credential = EmailAuthProvider.credential(currentUser.email, password);
    await reauthenticateWithCredential(currentUser, credential);
  };

  // Sauvegarde du nouvel email avec gestion de requires-recent-login
  const handleSaveEmail = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return alert("Aucun utilisateur connecté.");
    const mail = sanitizeInput(newEmail.trim());
    if (!mail) return alert("Veuillez saisir un email valide.");

    try {
      await firebaseUpdateEmail(currentUser, mail);
    } catch (err) {
      if (err.code === 'auth/requires-recent-login') {
        try {
          await reauthenticate(currentUser);
          await firebaseUpdateEmail(currentUser, mail);
        } catch (reauthErr) {
          console.error('Ré-authentification échouée :', reauthErr);
          return alert("Ré-authentification échouée : " + reauthErr.message);
        }
      } else {
        console.error('Erreur updateEmail:', err);
        return alert(err.message);
      }
    }

    // Mise à jour Firestore si updateEmail a réussi (writes — pas de read)
    try {
      // 1) Update collection "users"
      const userRef = doc(firestore, 'users', currentUser.uid);
      await updateDoc(userRef, { email: mail });

      // 2) Update collection "Satisfaction"
      const satisfactionRef = doc(firestore, 'Satisfaction', currentUser.uid);
      await updateDoc(satisfactionRef, { email: mail });

      // 3) Update collection "entreprises" (limit(10) max « sécurité » si jamais plusieurs docs)
      const entreprisesCol = collection(firestore, 'entreprises');
      const q = query(entreprisesCol, where('userId', '==', currentUser.uid), fsLimit(10)); // [COST-OPT]
      const entreprisesSnap = await getDocs(q);
      for (const docSnap of entreprisesSnap.docs) {
        const entRef = doc(firestore, 'entreprises', docSnap.id);
        await updateDoc(entRef, {
          createdBy: mail,
          email: mail
        });
      }

      // Mise à jour locale et UI
      const next = { email: mail, pseudo: user.pseudo };
      setUser(prev => (shallowEqual(prev, next) ? prev : next));
      localStorage.setItem('Settings', JSON.stringify(next));
      setIsEditingEmail(false);
      setNewEmail('');
      alert("Email mis à jour avec succès !");
    } catch (err) {
      console.error('Erreur Firestore update:', err);
      alert("Email mis à jour dans Auth, mais échec Firestore : " + err.message);
    }
  };

  // Modification du pseudo
  const handlePseudoChange = async () => {
    const uid = auth.currentUser?.uid;
    const txt = sanitizeInput(newPseudo.trim());
    if (!uid || !txt) return alert("Veuillez entrer un nom valide.");
    const userRef = doc(firestore, 'users', uid);
    await updateDoc(userRef, { pseudo: txt });
    const next = { ...user, pseudo: txt };
    setUser(prev => (shallowEqual(prev, next) ? prev : next)); // [COST-OPT]
    setIsEditingPseudo(false);
    // persiste local
    try { localStorage.setItem('Settings', JSON.stringify(next)); } catch {}
  };

  const handleSavePseudo = async () => {
    const uid = auth.currentUser?.uid;
    const txt = sanitizeInput(newPseudo.trim());
    if (!uid || !txt) return alert("Veuillez entrer un nom valide.");
    const userRef = doc(firestore, 'users', uid);
    await updateDoc(userRef, { pseudo: txt });
    const next = { ...user, pseudo: txt };
    setUser(prev => (shallowEqual(prev, next) ? prev : next)); // [COST-OPT]
    setShowModal(false);
    try { localStorage.setItem('Settings', JSON.stringify(next)); } catch {}
  };

  // Fetch invitations (mémo + TTL)
  const _fetchInvitations = useCallback(async (email) => {
    const safeEmail = sanitizeInput(email || '');
    if (!safeEmail) { setInvitations([]); return; }

    // cache 1h
    const k = invitesKey(safeEmail);
    const cached = localStorage.getItem(k);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (parsed?.items && parsed?.ts && Date.now() - parsed.ts < INVITES_TTL) {
          setInvitations(parsed.items);
          return; // [COST-OPT]
        }
      } catch {}
    }

    const q = query(
      collection(firestore, 'entreprises'),
      where('employeeEmail', '==', safeEmail),
      where('status', '==', 'pending'),
      fsLimit(30) // Firestore rule requires an explicit limit (<=50) for list queries
    );
    const snap = await getDocs(q);
    const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setInvitations(items);
    try { localStorage.setItem(k, JSON.stringify({ items, ts: Date.now() })); } catch {}
    // eslint-disable-next-line
  }, [firestore]);

  const debouncedFetchInvitations = useMemo(
    () => debounce(_fetchInvitations, 600), // [COST-OPT] throttle léger
    [_fetchInvitations]
  );

  // Déconnexion
  const handleLogout = async () => {
    try {
      await logoutSafely(auth);
    } catch (err) {
      console.error("Erreur déconnexion:", err);
    } finally {
      try {
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userRole');
      } catch {}
      window.location.href = '/';
    }
  };

  /* ================================
     UI (styles mémoïsés) — même design que ClientSettings
  ================================= */
  const pseudoTextStyle = useMemo(() => ({
    textAlign: 'left',
    fontSize: 20,
    fontWeight: 700,
    letterSpacing: '-0.015em',
    color: 'var(--eb-text)',
  }), []);

  return (
    <div className="dashboard-container merchant-settings-page">
      <MerchantNavbar />
      <div className="cs-wrap">
        <header className="eb-page-header">
          <div>
            <h1 className="eb-page-title">Profil</h1>
            <p className="eb-page-subtitle">Informations de votre entreprise et préférences</p>
          </div>
        </header>

        {/* Photo de profil + nom */}
        <section className="cs-card cs-profile">
          <button
            type="button"
            className="profile-photo-container cs-avatar"
            onClick={() => fileInputRef.current?.click()}
            aria-label="Modifier le logo"
          >
            {profilePhotoURL ? (
              <img
                src={profilePhotoURL}
                alt="Profil"
                className="profile-photo"
              />
            ) : (
              <FaUser aria-hidden="true" />
            )}
            <span className="cs-avatar__edit" aria-hidden="true"><FiEdit2 size={13} /></span>
          </button>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/*"
            onChange={handleProfilePhotoChange}
          />
          <div className="cs-profile__name">
            <AccountSwitcher
              label={user?.pseudo || user?.email || 'Mon compte'}
              labelStyle={pseudoTextStyle}
            />
          </div>
        </section>

        <div className="eb-grid-2 cs-grid">
          {/* ====== CARD PRINCIPALE SETTINGS ====== */}
          <section className="settings-container cs-card cs-card--sectioned">
            <div className="eb-card__head">
              <h2 className="eb-card__title cs-section-title">Informations</h2>
            </div>

            {/* Modale définir pseudo (nom d'entreprise) */}
            {showModal && (
              <div className="cs-overlay">
                <div className="cs-modal eb-fade-up" role="dialog" aria-modal="true">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="cs-modal__close"
                    aria-label="Fermer"
                  >
                    &times;
                  </button>
                  <h2 className="cs-modal__title">
                    Définir un nom d'entreprise
                  </h2>
                  <input
                    type="text"
                    className="eb-input"
                    value={newPseudo}
                    onChange={e => setNewPseudo(e.target.value)}
                    placeholder="Votre nom d'entreprise"
                  />
                  <button
                    type="button"
                    className="eb-btn eb-btn--primary eb-btn--lg eb-btn--block"
                    onClick={handleSavePseudo}
                  >
                    Enregistrer
                  </button>
                </div>
              </div>
            )}

            <div className="eb-card__body user-info cs-rows">
              {/* EMAIL */}
              <div className="row-email cs-row">
                <span className="cs-row__label">Email</span>
                {isEditingEmail ? (
                  <div className="cs-row__edit">
                    <input
                      type="email"
                      className="eb-input"
                      value={newEmail}
                      onChange={e => setNewEmail(e.target.value)}
                      placeholder="Nouvel email"
                      aria-label="Nouvel email"
                      inputMode="email"
                    />
                    <div className="cs-row__actions">
                      <button
                        type="button"
                        className="eb-btn eb-btn--primary"
                        onClick={handleSaveEmail}
                      >
                        Enregistrer
                      </button>
                      <button
                        type="button"
                        className="eb-btn eb-btn--secondary cs-icon-btn"
                        onClick={() => setIsEditingEmail(false)}
                        aria-label="Annuler"
                        title="Annuler"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ) : (
                  <span className="inline-center-mobile cs-row__value">
                    <span className="cs-row__text">{user.email}</span>
                    <button
                      type="button"
                      className="eb-btn eb-btn--secondary cs-icon-btn"
                      onClick={() => {
                        setIsEditingEmail(true);
                        setNewEmail(user.email);
                      }}
                      aria-label="Modifier"
                      title="Modifier"
                    >
                      <FiEdit2 size={16} />
                    </button>
                  </span>
                )}
              </div>

              {/* PSEUDO (Nom d'entreprise) */}
              <div className="row-pseudo cs-row">
                <span className="cs-row__label">Entreprise</span>
                {isEditingPseudo ? (
                  <div className="cs-row__edit">
                    <input
                      type="text"
                      className="eb-input"
                      value={newPseudo}
                      onChange={e => setNewPseudo(e.target.value)}
                      placeholder="Nom d'entreprise"
                      aria-label="Nom d'entreprise"
                    />
                    <div className="cs-row__actions">
                      <button
                        type="button"
                        className="eb-btn eb-btn--primary"
                        onClick={handlePseudoChange}
                      >
                        Mettre à jour
                      </button>
                    </div>
                  </div>
                ) : (
                  <span className="inline-center-mobile cs-row__value">
                    <span className="cs-row__text">{user.pseudo}</span>
                    <button
                      type="button"
                      className="eb-btn eb-btn--secondary cs-icon-btn"
                      onClick={() => {
                        setIsEditingPseudo(true);
                        setNewPseudo(user.pseudo);
                      }}
                      aria-label="Modifier"
                      title="Modifier"
                    >
                      <FiEdit2 size={16} />
                    </button>
                  </span>
                )}
              </div>
            </div>
          </section>

          {/* Abonnement */}
          <section className="cs-card cs-card--sectioned cs-sub">
            <div className="eb-card__head">
              <h2 className="eb-card__title cs-section-title">Abonnement</h2>
            </div>
            <div className="eb-card__body">
              <div className="eb-row">
                <div className="eb-row__main">
                  <p className="cs-muted" style={{ margin: 0 }}>Consultez et gérez votre formule Ecobill</p>
                </div>
                <div className="eb-row__actions">
                  <Link to="/merchantabonnement" className="eb-btn eb-btn--secondary">
                    Mon abonnement
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Préférences */}
          <section className="cs-card cs-card--sectioned">
            <div className="eb-card__head">
              <h2 className="eb-card__title cs-section-title">Préférences</h2>
            </div>
            <div className="eb-card__body">
              {/* Sélecteur de langue */}
              <div id="language-selector" className="cs-lang">
                <LanguageSelector />
                <div className="cs-muted">Changer la langue de l'interface</div>
              </div>
            </div>
          </section>

          {/* Documents juridiques */}
          <section className="cs-card cs-card--sectioned">
            <div className="eb-card__head">
              <h2 className="eb-card__title cs-section-title">Documents juridiques</h2>
            </div>
            <div className="eb-card__body cs-legal">
              {/* CGU + CGV */}
              <button
                type="button"
                className="cs-doc-link"
                onClick={() => navigate('/legal/cgu')}
              >
                Conditions générales d'utilisation <FiChevronRight aria-hidden="true" />
              </button>
              <button
                type="button"
                className="cs-doc-link"
                onClick={() => navigate('/legal/cgv')}
              >
                Conditions générales de vente <FiChevronRight aria-hidden="true" />
              </button>
            </div>
          </section>
        </div>

        {/* Déconnexion */}
        <section className="cs-card cs-session">
          <div className="eb-row">
            <div className="eb-row__main">
              <p className="cs-session__title">Session</p>
              <p className="cs-muted" style={{ margin: 0 }}>Se déconnecter de ce compte sur cet appareil.</p>
            </div>
            <div className="eb-row__actions logout-container">
              <button
                type="button"
                className="logout-button eb-btn eb-btn--secondary cs-danger-btn"
                onClick={handleLogout}
              >
                Déconnexion
              </button>
            </div>
          </div>
        </section>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
.dashboard-container.merchant-settings-page { align-items: stretch; }
.merchant-settings-page .cs-wrap { width: 100%; display: flex; flex-direction: column; gap: 20px; }
.merchant-settings-page .cs-grid { align-items: stretch; }
.merchant-settings-page .cs-wrap .eb-page-header { margin-bottom: 8px; }
.merchant-settings-page .cs-wrap .eb-page-title { margin: 0; text-align: left; }

.merchant-settings-page .cs-card {
  display: block;
  background: var(--eb-surface);
  border: 1px solid var(--eb-border);
  border-radius: var(--eb-radius-lg);
  box-shadow: var(--eb-shadow-sm);
  padding: 24px;
  margin: 0;
  width: 100%;
  max-width: none;
  text-align: left;
}
.merchant-settings-page .cs-section-title { margin: 0; text-align: left; font-size: 15px; font-weight: 650; color: var(--eb-text); }
.merchant-settings-page .cs-card.cs-card--sectioned { padding: 0; }
.merchant-settings-page .cs-card--sectioned > .eb-card__body { padding: 8px 20px 12px; }

/* Profil */
.merchant-settings-page .cs-profile { display: flex; align-items: center; gap: 20px; }
.merchant-settings-page .cs-avatar {
  position: relative; flex-shrink: 0; width: 88px; height: 88px; padding: 0; border-radius: 50%;
  border: 3px solid var(--eb-surface); background: var(--eb-brand-50); box-shadow: var(--eb-shadow-md);
  color: var(--eb-brand-300); font-size: 36px; display: inline-flex; align-items: center; justify-content: center; cursor: pointer;
}
.merchant-settings-page .cs-avatar img { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; display: block; }
.merchant-settings-page .cs-avatar:focus-visible { outline: none; box-shadow: var(--eb-ring); }
.merchant-settings-page .cs-avatar__edit {
  position: absolute; right: -2px; bottom: -2px; width: 30px; height: 30px; border-radius: 50%;
  background: var(--eb-brand); color: #fff; border: 2px solid var(--eb-surface);
  display: inline-flex; align-items: center; justify-content: center;
}
.merchant-settings-page .cs-profile__name { min-width: 0; display: flex; }

/* Lignes d'info */
.merchant-settings-page .cs-rows { display: flex; flex-direction: column; width: 100%; }
.merchant-settings-page .cs-row {
  display: grid; grid-template-columns: 140px minmax(0, 1fr); align-items: center; gap: 12px;
  padding: 14px 0; border-bottom: 1px solid var(--eb-border);
}
.merchant-settings-page .cs-rows > .cs-row:last-of-type { border-bottom: 0; }
.merchant-settings-page .cs-row__label { font-size: 14px; font-weight: 600; color: var(--eb-text-2); }
.merchant-settings-page .cs-row__value { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-width: 0; }
.merchant-settings-page .cs-row__text { min-width: 0; overflow-wrap: anywhere; color: var(--eb-text); font-size: 15px; }
.merchant-settings-page .cs-row__edit { display: flex; flex-direction: column; gap: 10px; min-width: 0; }
.merchant-settings-page .cs-row__actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.merchant-settings-page .cs-icon-btn { width: 40px; min-width: 40px; padding: 0; flex-shrink: 0; color: var(--eb-brand); }
.merchant-settings-page .cs-danger-btn { color: var(--eb-danger); }
.merchant-settings-page .cs-danger-btn:hover:not(:disabled) { background: var(--eb-danger-bg); color: var(--eb-danger-hover); border-color: #fbd0cb; }

/* Session */
.merchant-settings-page .cs-card.cs-session { padding: 4px 20px; }
.merchant-settings-page .cs-session__title { margin: 0; font-size: 14.5px; font-weight: 600; color: var(--eb-text); }
.merchant-settings-page .cs-session .logout-container { margin: 0; width: auto; display: flex; }

/* Langue + pied */
.merchant-settings-page .cs-lang { display: flex; flex-direction: column; align-items: flex-start; gap: 8px; padding: 6px 0; }
.merchant-settings-page .cs-muted { color: var(--eb-muted); font-size: 13.5px; }
.merchant-settings-page .cs-legal { display: flex; flex-direction: column; }
.merchant-settings-page .cs-doc-link {
  display: flex; align-items: center; justify-content: space-between; gap: 12px; width: 100%; min-height: 52px; padding: 0;
  background: none; border: 0; border-bottom: 1px solid var(--eb-border); color: var(--eb-text);
  font-size: 14.5px; font-weight: 500; text-align: left; cursor: pointer;
}
.merchant-settings-page .cs-doc-link:last-child { border-bottom: 0; }
.merchant-settings-page .cs-doc-link:hover { color: var(--eb-brand); }
.merchant-settings-page .cs-doc-link svg { color: var(--eb-subtle); flex-shrink: 0; }
#language-selector > :first-child { margin-top: 0; }
#language-selector h3 { margin: 0 0 10px; text-align: left; font-size: 17px; font-weight: 700; color: var(--eb-text); }
#language-selector h3:empty { display: none; }
#language-selector select {
  appearance: none; -webkit-appearance: none; -moz-appearance: none;
  min-height: 42px; padding: 0 38px 0 14px; border-radius: var(--eb-radius-sm);
  border: 1px solid var(--eb-border-strong); background-color: var(--eb-surface); color: var(--eb-text); font-size: 14.5px;
  background-image: linear-gradient(45deg, transparent 50%, var(--eb-muted) 50%), linear-gradient(135deg, var(--eb-muted) 50%, transparent 50%);
  background-position: calc(100% - 18px) 50%, calc(100% - 13px) 50%;
  background-size: 5px 5px, 5px 5px; background-repeat: no-repeat;
}
#language-selector button {
  min-height: 40px; padding: 0 14px; border-radius: var(--eb-radius-sm);
  border: 1px solid var(--eb-border-strong); background: var(--eb-surface); color: var(--eb-text-2); margin: 0 4px 0 0;
}
#language-selector button:hover { background: var(--eb-surface-2); }
#language-selector .active, #language-selector .selected { color: var(--eb-brand); border-color: var(--eb-brand-300); background: var(--eb-brand-50); }

/* Modale */
.merchant-settings-page .cs-overlay {
  position: fixed; inset: 0; z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 16px;
  background: rgba(15, 35, 56, 0.55); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
}
.merchant-settings-page .cs-modal {
  position: relative; width: 100%; max-width: 440px; max-height: calc(100dvh - 32px); overflow-y: auto;
  background: var(--eb-surface); border-radius: var(--eb-radius-xl); box-shadow: var(--eb-shadow-lg);
  padding: 24px; display: flex; flex-direction: column; gap: 14px; text-align: left;
}
.merchant-settings-page .cs-modal__title { margin: 0 0 4px; padding-right: 44px; font-size: 20px; font-weight: 700; letter-spacing: -0.02em; text-align: left; color: var(--eb-text); }
.merchant-settings-page .cs-modal__close {
  position: absolute; top: 14px; right: 14px; width: 40px; height: 40px; display: inline-flex; align-items: center; justify-content: center;
  border: 0; border-radius: var(--eb-radius-sm); background: transparent; color: var(--eb-muted); font-size: 22px; cursor: pointer;
}
.merchant-settings-page .cs-modal__close:hover { background: var(--eb-surface-2); color: var(--eb-text); }

@media (max-width: 600px) {
  .merchant-settings-page .cs-card { padding: 18px 16px; }
  .merchant-settings-page .cs-profile { gap: 16px; }
  .merchant-settings-page .cs-avatar { width: 72px; height: 72px; font-size: 30px; }
  .merchant-settings-page .cs-row { grid-template-columns: 1fr; gap: 6px; }
  .merchant-settings-page .cs-card.cs-card--sectioned { padding: 0; }
  .merchant-settings-page .cs-card--sectioned > .eb-card__body { padding: 6px 16px 10px; }
  .merchant-settings-page .cs-card.cs-session { padding: 4px 16px; }
  .merchant-settings-page .cs-sub .eb-row__actions,
  .merchant-settings-page .cs-sub .eb-btn,
  .merchant-settings-page .cs-session .logout-container,
  .merchant-settings-page .cs-session .logout-button { width: 100%; }
}
`}} />
    </div>
  );
};

export default MerchantSettings;
